/**
 * Shopify认证工具函数
 * 用于提取URL中的验证参数并添加到请求头中
 */

export interface ShopifyAuthParams {
  shop: string
  hmac: string
  host: string
  id_token: string
  session: string
  timestamp: string
  embedded: string
  locale: string
}

// 存储Shopify认证参数的键名
const SHOPIFY_AUTH_STORAGE_KEY = 'shopify_auth_params'

/**
 * 将Shopify认证参数存储到localStorage
 */
function storeShopifyAuthParams(params: ShopifyAuthParams): void {
  try {
    localStorage.setItem(SHOPIFY_AUTH_STORAGE_KEY, JSON.stringify(params))
  } catch (error) {
    console.error('Error storing Shopify auth params:', error)
  }
}

/**
 * 从localStorage获取Shopify认证参数
 */
function getStoredShopifyAuthParams(): ShopifyAuthParams | null {
  try {
    const stored = localStorage.getItem(SHOPIFY_AUTH_STORAGE_KEY)
    if (!stored) return null
    
    const params = JSON.parse(stored) as ShopifyAuthParams
    // 验证参数是否完整
    const requiredParams = ['shop', 'hmac', 'host', 'id_token', 'session', 'timestamp']
    const hasAllRequired = requiredParams.every(param => params[param as keyof ShopifyAuthParams] && params[param as keyof ShopifyAuthParams].length > 0)
    
    return hasAllRequired ? params : null
  } catch (error) {
    console.error('Error retrieving stored Shopify auth params:', error)
    return null
  }
}

/**
 * 清除存储的Shopify认证参数
 */
function clearStoredShopifyAuthParams(): void {
  try {
    localStorage.removeItem(SHOPIFY_AUTH_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing stored Shopify auth params:', error)
  }
}

/**
 * 从URL中提取Shopify认证参数
 */
export function extractShopifyAuthParams(): ShopifyAuthParams | null {
  try {
    const urlParams = new URLSearchParams(window.location.search)

    const requiredParams = ['shop', 'hmac', 'host', 'id_token', 'session', 'timestamp']
    const hasAllRequired = requiredParams.every(param => urlParams.has(param))

    if (!hasAllRequired) {
      // 如果URL中没有参数，尝试从存储中获取
      return getStoredShopifyAuthParams()
    }

    const params = {
      shop: urlParams.get('shop') || '',
      hmac: urlParams.get('hmac') || '',
      host: urlParams.get('host') || '',
      id_token: urlParams.get('id_token') || '',
      session: urlParams.get('session') || '',
      timestamp: urlParams.get('timestamp') || '',
      embedded: urlParams.get('embedded') || '',
      locale: urlParams.get('locale') || ''
    }

    // 将参数存储到localStorage中，以便后续页面使用
    storeShopifyAuthParams(params)
    
    return params
  } catch (error) {
    console.error('Error extracting Shopify auth params:', error)
    // 出错时也尝试从存储中获取
    return getStoredShopifyAuthParams()
  }
}

/**
 * 将Shopify认证参数转换为请求头
 */
export function getShopifyAuthHeaders(): Record<string, string> {
  const authParams = extractShopifyAuthParams()

  if (!authParams) {
    console.warn('No Shopify auth params available for headers')
    return {}
  }

  const headers = {
    'x-shopify-shop-domain': authParams.shop,
    'x-shopify-hmac-sha256': authParams.hmac,
    'x-shopify-host': authParams.host,
    'x-shopify-id-token': authParams.id_token,
    'x-shopify-session': authParams.session,
    'x-shopify-timestamp': authParams.timestamp,
    'x-shopify-embedded': authParams.embedded,
    'x-shopify-locale': authParams.locale
  }

  // 调试信息：记录头部是否完整
  const hasRequiredHeaders = headers['x-shopify-shop-domain'] && 
                            headers['x-shopify-hmac-sha256'] && 
                            headers['x-shopify-host'] && 
                            headers['x-shopify-id-token'] && 
                            headers['x-shopify-session'] && 
                            headers['x-shopify-timestamp']
  
  if (hasRequiredHeaders) {
    console.log('Shopify auth headers generated successfully')
  } else {
    console.warn('Shopify auth headers incomplete:', headers)
  }

  return headers
}

/**
 * 验证Shopify认证参数是否完整
 */
export function validateShopifyAuthParams(): boolean {
  const authParams = extractShopifyAuthParams()

  if (!authParams) {
    return false
  }

  // 检查必要的参数是否存在且不为空
  const requiredParams = ['shop', 'hmac', 'host', 'id_token', 'session', 'timestamp']
  return requiredParams.every(param => authParams[param as keyof ShopifyAuthParams] && authParams[param as keyof ShopifyAuthParams].length > 0)
}

/**
 * 获取当前商店ID（从shop参数中提取）
 */
export function getCurrentShopId(): string {
  const authParams = extractShopifyAuthParams()

  if (!authParams?.shop) {
    return ''
  }

  // 从 shop.myshopify.com 格式中提取商店ID
  return authParams.shop.split('.')[0] || ''
}

/**
 * 解码host参数（Base64解码）
 */
export function decodeShopifyHost(): string {
  const authParams = extractShopifyAuthParams()

  if (!authParams?.host) {
    return ''
  }

  try {
    return atob(authParams.host)
  } catch (error) {
    console.error('Error decoding Shopify host:', error)
    return ''
  }
}

/**
 * 检查是否在Shopify环境中运行
 */
export function isInShopifyEnvironment(): boolean {
  return validateShopifyAuthParams()
}

/**
 * 初始化Shopify认证参数
 * 在应用启动时调用，确保参数被正确存储
 */
export function initializeShopifyAuth(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const requiredParams = ['shop', 'hmac', 'host', 'id_token', 'session', 'timestamp']
    const hasAllRequired = requiredParams.every(param => urlParams.has(param))

    if (hasAllRequired) {
      const params = {
        shop: urlParams.get('shop') || '',
        hmac: urlParams.get('hmac') || '',
        host: urlParams.get('host') || '',
        id_token: urlParams.get('id_token') || '',
        session: urlParams.get('session') || '',
        timestamp: urlParams.get('timestamp') || '',
        embedded: urlParams.get('embedded') || '',
        locale: urlParams.get('locale') || ''
      }
      storeShopifyAuthParams(params)
      console.log('Shopify auth params initialized and stored')
    } else {
      // 检查是否有存储的参数
      const storedParams = getStoredShopifyAuthParams()
      if (storedParams) {
        console.log('Using stored Shopify auth params')
      } else {
        console.warn('No Shopify auth params found in URL or storage')
      }
    }
  } catch (error) {
    console.error('Error initializing Shopify auth:', error)
  }
}

/**
 * 清除所有Shopify认证数据
 */
export function clearShopifyAuth(): void {
  clearStoredShopifyAuthParams()
  console.log('Shopify auth data cleared')
}

/**
 * 获取完整的认证信息（用于调试）
 */
export function getShopifyAuthInfo(): {
  isValid: boolean
  shopId: string
  host: string
  hasAllParams: boolean
  hasStoredParams: boolean
  params: ShopifyAuthParams | null
} {
  const authParams = extractShopifyAuthParams()
  const isValid = validateShopifyAuthParams()
  const shopId = getCurrentShopId()
  const host = decodeShopifyHost()
  const hasStoredParams = !!getStoredShopifyAuthParams()

  return {
    isValid,
    shopId,
    host,
    hasAllParams: !!authParams,
    hasStoredParams,
    params: authParams
  }
}

/**
 * 测试Shopify认证参数持久化
 * 在浏览器控制台中调用此函数来测试
 */
export function testShopifyAuthPersistence(): void {
  console.log('=== Shopify Auth Persistence Test ===')
  
  // 测试当前URL参数
  const urlParams = new URLSearchParams(window.location.search)
  console.log('Current URL params:', Object.fromEntries(urlParams.entries()))
  
  // 测试存储的参数
  const storedParams = getStoredShopifyAuthParams()
  console.log('Stored params:', storedParams)
  
  // 测试提取的参数
  const extractedParams = extractShopifyAuthParams()
  console.log('Extracted params:', extractedParams)
  
  // 测试生成的头部
  const headers = getShopifyAuthHeaders()
  console.log('Generated headers:', headers)
  
  // 测试认证信息
  const authInfo = getShopifyAuthInfo()
  console.log('Auth info:', authInfo)
  
  console.log('=== Test Complete ===')
}

// 在开发环境中自动暴露测试函数
if (import.meta.env.DEV) {
  (window as any).testShopifyAuth = testShopifyAuthPersistence
  ;(window as any).clearShopifyAuth = clearShopifyAuth
  ;(window as any).getShopifyAuthInfo = getShopifyAuthInfo
}
